/**
 * AtBatLogSection Component
 *
 * The prospect's at-bat log, rendered inside the notes tab.
 *
 * The point of the section is the longitudinal read: a coach sees the same player
 * repeatedly over weeks or months, and until now each viewing produced a separate
 * free-text note with no way to read them as a sequence. So entries are grouped by
 * game, newest game first, and ordered within a game by the batting order rather
 * than by when they were typed.
 */

import { useCallback, useEffect, useState } from "react";
import { Button } from "../../../../../components/Button";
import { ConfirmModal } from "../../../../../components/ConfirmModal";
import { AddIcon } from "../../../../../assets/AddIcon";
import {
  atBatResultAbbreviation,
  atBatResultLabel,
  isOnBaseResult,
} from "../../../../../constants/atBatResults";
import {
  createAtBat,
  deleteAtBat,
  getAtBatLog,
  updateAtBat,
} from "../../../../../services/at-bats";
import {
  formatAtBatSummary,
  formatAtBatTime,
  formatGameDate,
  groupAtBatsByGame,
} from "../../../../../utils/atBatLog";
import { getSafeErrorMessage } from "../../../../../utils/errorHandler";
import type { AtBat, AtBatLog } from "../../../../../types/at-bats";
import { AtBatForm, type AtBatFormValues } from "./AtBatForm";
import "./AtBatLogSection.css";

interface AtBatLogSectionProps {
  prospectId: string;
}

export function AtBatLogSection({ prospectId }: AtBatLogSectionProps) {
  const [log, setLog] = useState<AtBatLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AtBat | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<AtBat | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const next = await getAtBatLog(prospectId);
    setLog(next);
  }, [prospectId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    // Reset per-prospect UI state so switching prospects in the viewer panel
    // cannot leave a form open over someone else's log.
    setFormOpen(false);
    setEditing(null);
    setPendingDelete(null);

    getAtBatLog(prospectId)
      .then((next) => {
        if (cancelled) return;
        setLog(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(getSafeErrorMessage(err, "Failed to load the at-bat log"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [prospectId]);

  const openAdd = () => {
    setEditing(null);
    setSubmitError(null);
    setFormOpen(true);
  };

  const openEdit = (atBat: AtBat) => {
    setEditing(atBat);
    setSubmitError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setSubmitError(null);
  };

  const handleSubmit = async (values: AtBatFormValues) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (editing) {
        await updateAtBat(prospectId, editing.id, {
          result: values.result,
          details: values.details,
          occurredAt: values.occurredAt,
          occurredAtOffsetMinutes: values.occurredAtOffsetMinutes,
          sequenceNumber: values.sequenceNumber,
        });
      } else {
        await createAtBat(prospectId, {
          result: values.result,
          details: values.details,
          occurredAt: values.occurredAt,
          occurredAtOffsetMinutes: values.occurredAtOffsetMinutes,
        });
      }
      // Re-read rather than splice locally: the server owns sequence assignment
      // and the summary, so a local insert would show the wrong batting order
      // until the next load.
      await refresh();
      closeForm();
    } catch (err) {
      setSubmitError(getSafeErrorMessage(err, "Failed to save the at-bat"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAtBat(prospectId, pendingDelete.id);
      await refresh();
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(getSafeErrorMessage(err, "Failed to delete the at-bat"));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const games = groupAtBatsByGame(log?.items ?? []);
  const summaryLine = log ? formatAtBatSummary(log.summary) : "";

  return (
    <div className="at-bat-log">
      <div className="at-bat-log__header">
        <div>
          <h4 className="at-bat-log__title">At-Bat Log</h4>
          {summaryLine && (
            <p className="at-bat-log__summary" data-testid="at-bat-summary">
              {summaryLine}
            </p>
          )}
        </div>
        {!formOpen && (
          <Button variant="primary" onClick={openAdd} aria-label="Log an at-bat">
            <AddIcon />
            Log At-Bat
          </Button>
        )}
      </div>

      {loadError && (
        <div className="at-bat-log__error" role="alert">
          {loadError}
        </div>
      )}

      {deleteError && (
        <div className="at-bat-log__error" role="alert">
          {deleteError}
          <button
            type="button"
            className="at-bat-log__error-dismiss"
            onClick={() => setDeleteError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {formOpen && (
        <>
          {submitError && (
            <div className="at-bat-log__error" role="alert">
              {submitError}
            </div>
          )}
          <AtBatForm
            key={editing?.id ?? "new"}
            atBat={editing ?? undefined}
            submitting={submitting}
            onSubmit={handleSubmit}
            onCancel={closeForm}
          />
        </>
      )}

      {loading ? (
        <div className="at-bat-log__loading">Loading at-bats…</div>
      ) : games.length === 0 ? (
        !loadError && (
          <div className="at-bat-log__empty">
            <p className="at-bat-log__empty-title">No at-bats logged yet</p>
            <p className="at-bat-log__empty-subtext">
              Log a plate appearance and it joins this player's running sequence
              across every viewing.
            </p>
          </div>
        )
      ) : (
        <ol className="at-bat-log__games">
          {games.map((game) => (
            <li key={game.gameDate} className="at-bat-log__game">
              <h5 className="at-bat-log__game-date">{formatGameDate(game.gameDate)}</h5>
              <ol className="at-bat-log__entries">
                {game.atBats.map((atBat) => (
                  <li key={atBat.id} className="at-bat-log__entry">
                    <span className="at-bat-log__sequence" aria-hidden>
                      {atBat.sequenceNumber}
                    </span>
                    <span
                      className={`at-bat-log__result ${
                        isOnBaseResult(atBat.result)
                          ? "at-bat-log__result--on-base"
                          : "at-bat-log__result--out"
                      }`}
                      title={atBatResultLabel(atBat.result)}
                    >
                      {atBatResultAbbreviation(atBat.result)}
                    </span>
                    <span className="at-bat-log__entry-body">
                      <span className="at-bat-log__entry-label">
                        {atBatResultLabel(atBat.result)}
                      </span>
                      {atBat.details && (
                        <span className="at-bat-log__entry-details">{atBat.details}</span>
                      )}
                      <span className="at-bat-log__entry-meta">
                        {formatAtBatTime(atBat.occurredAt, atBat.occurredAtOffsetMinutes)}
                        {atBat.recordedByName ? ` · ${atBat.recordedByName}` : ""}
                      </span>
                    </span>
                    <span className="at-bat-log__entry-actions">
                      <button
                        type="button"
                        className="at-bat-log__entry-action"
                        onClick={() => openEdit(atBat)}
                        aria-label={`Edit at-bat ${atBat.sequenceNumber} on ${formatGameDate(
                          game.gameDate,
                        )}`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="at-bat-log__entry-action at-bat-log__entry-action--danger"
                        onClick={() => setPendingDelete(atBat)}
                        aria-label={`Delete at-bat ${atBat.sequenceNumber} on ${formatGameDate(
                          game.gameDate,
                        )}`}
                      >
                        Delete
                      </button>
                    </span>
                  </li>
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}

      {log?.hasMore && (
        <p className="at-bat-log__truncated">
          Showing the {log.items.length} most recent of {log.totalCount} at-bats.
        </p>
      )}

      <ConfirmModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        busy={deleting}
        title="Delete this at-bat?"
        message={
          pendingDelete
            ? `${atBatResultLabel(pendingDelete.result)} on ${formatGameDate(
                pendingDelete.gameDate,
              )} will be removed from the log.`
            : ""
        }
        confirmText="Delete"
      />
    </div>
  );
}
